import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, TextField, InputAdornment, Paper, Typography, Chip, CircularProgress, ClickAwayListener,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BuildIcon from "@mui/icons-material/Build";
import PersonIcon from "@mui/icons-material/Person";
import api from "../../utils/api";

const lightFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
  },
};

const darkFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "rgba(255,255,255,0.15)",
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
    "&.Mui-focused fieldset": { borderColor: "#fff" },
  },
  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.75)", opacity: 1 },
};

// Instant-dropdown search across Green Sheets (work orders) and members/properties.
// variant "light" (default) is for a white background; "dark" is for embedding
// directly in the blue Dashboard top bar.
const GlobalSearch = ({ variant = "light" }) => {
  const fieldSx = variant === "dark" ? darkFieldSx : lightFieldSx;
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setWorkOrders([]);
      setMembers([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [woRes, memberRes] = await Promise.all([
          api.get(`/work-orders/search?q=${encodeURIComponent(query)}`),
          api.get(`/membersfilter?search=${encodeURIComponent(query)}`),
        ]);
        setWorkOrders(woRes.data || []);
        setMembers((memberRes.data?.members || []).slice(0, 6));
        setOpen(true);
      } catch {
        setWorkOrders([]);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleClose = () => setOpen(false);

  const goToWorkOrder = (item) => {
    setQuery("");
    setOpen(false);
    navigate(item.link);
  };

  const goToMember = (m) => {
    setQuery("");
    setOpen(false);
    navigate(`/home/clubmembers/${m.ClubMemberCode}`);
  };

  const hasResults = workOrders.length > 0 || members.length > 0;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative", width: "100%", maxWidth: variant === "dark" ? 280 : 420 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={variant === "dark" ? "Search…" : "Search Green Sheets, members, addresses…"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim() && setOpen(true)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: variant === "dark" ? "rgba(255,255,255,0.75)" : "#9aa5b4" }} />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={14} />
              </InputAdornment>
            ) : null,
          }}
          sx={fieldSx}
        />
        {open && (
          <Paper elevation={4} sx={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 1201,
            borderRadius: "12px", overflow: "hidden", maxHeight: 360, overflowY: "auto",
          }}>
            {!hasResults && !loading && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: "#9aa5b4" }}>No matches found.</Typography>
              </Box>
            )}

            {workOrders.length > 0 && (
              <Box sx={{ py: 0.5 }}>
                <Typography variant="caption" sx={{ px: 2, color: "#9aa5b4", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Green Sheets
                </Typography>
                {workOrders.map((wo) => (
                  <Box key={wo.id} onClick={() => goToWorkOrder(wo)}
                    sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1, cursor: "pointer",
                      "&:hover": { backgroundColor: "rgba(47,99,173,0.05)" } }}>
                    <BuildIcon sx={{ fontSize: 18, color: "#2f63ad", flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        #{wo.id} — {wo.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#9aa5b4" }}>{wo.subtitle}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {members.length > 0 && (
              <Box sx={{ py: 0.5, borderTop: workOrders.length > 0 ? "1px solid #f0f0f0" : "none" }}>
                <Typography variant="caption" sx={{ px: 2, color: "#9aa5b4", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Members & Properties
                </Typography>
                {members.map((m) => (
                  <Box key={m.ClubMemberCode} onClick={() => goToMember(m)}
                    sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1, cursor: "pointer",
                      "&:hover": { backgroundColor: "rgba(47,99,173,0.05)" } }}>
                    <PersonIcon sx={{ fontSize: 18, color: "#2f63ad", flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                          {m.FirstName} {m.LastName}
                        </Typography>
                        <Chip label={`#${m.ClubMemberCode}`} size="small"
                          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, fontFamily: "monospace",
                            backgroundColor: "rgba(47,99,173,0.1)", color: "#2f63ad" }} />
                      </Box>
                      {m.FullAddress && (
                        <Typography variant="caption" sx={{ color: "#9aa5b4" }}>{m.FullAddress}</Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
